import { useState } from "react";
import {
  Alert,
  AlertIcon,
  Flex,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Image,
  Text,
  Box,
  Link,
  FormControl,
  FormErrorMessage,
  InputRightElement,
} from "@chakra-ui/react";
import { FaUserAlt, FaLock } from "react-icons/fa";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { setToken } from "../Auth.js";
import logo from "../images/logo2.png";
import API_PATH from "../env";
import { clientFieldErrors, readApiError } from "../authHelpers.js";

const TOKEN_GUIDE_KEY = "worryless_show_token_guide";

export default function Signup() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    const errors = clientFieldErrors({
      email,
      password,
      firstName,
      lastName,
      mode: "signup",
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      const response = await fetch(`${API_PATH}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          username: email.trim(),
          password,
        }),
      });
      if (!response.ok) {
        setFormError(await readApiError(response));
        return;
      }
      const body = await response.json();
      setToken(body.token);
      try {
        localStorage.setItem(TOKEN_GUIDE_KEY, "1");
      } catch {
        // ignore storage failures
      }
      navigate(`/${body.user_id}/todos`, { state: { showTokenGuide: true } });
    } catch {
      setFormError("Network error. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      width="100wh"
      minH="100vh"
      backgroundColor="gray.200"
      justifyContent="center"
      alignItems="center"
      px={4}
    >
      <Stack flexDir="column" mb="2" justifyContent="center" alignItems="center" w="100%" maxW="468px">
        <Flex align="center" gap={2} justify="center" mb={2}>
          <Image src={logo} boxSize="60px" alt="" />
          <Text fontSize="3xl" fontWeight="bold">
            WorryLess
          </Text>
        </Flex>
        <Box w="100%" backgroundColor="whiteAlpha.900" boxShadow="md" p={4} borderRadius="md">
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {formError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {formError}
                </Alert>
              ) : null}
              <FormControl isInvalid={Boolean(fieldErrors.email)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUserAlt color="gray" />
                  </InputLeftElement>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isDisabled={busy}
                  />
                </InputGroup>
                <FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(fieldErrors.firstName)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUserAlt color="gray" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    autoComplete="given-name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    isDisabled={busy}
                  />
                </InputGroup>
                <FormErrorMessage>{fieldErrors.firstName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(fieldErrors.lastName)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUserAlt color="gray" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    autoComplete="family-name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    isDisabled={busy}
                  />
                </InputGroup>
                <FormErrorMessage>{fieldErrors.lastName}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(fieldErrors.password)}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaLock color="gray" />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Password (8+ characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isDisabled={busy}
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)} type="button">
                      {showPassword ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{fieldErrors.password}</FormErrorMessage>
              </FormControl>
              <Button type="submit" colorScheme="teal" width="full" isLoading={busy} loadingText="Creating account…">
                Create account
              </Button>
            </Stack>
          </form>
        </Box>
        <Text fontSize="sm">
          Already have an account?{" "}
          <Link as={RouterLink} to="/login" color="teal.500">
            Sign in
          </Link>
        </Text>
      </Stack>
    </Flex>
  );
}
